import { useState, useRef, useEffect, forwardRef } from "react";
import { cn } from "../../lib/utils";

/**
 * Otomatik tamamlama özelliği olan input bileşeni
 * @param {Object} props - Bileşen özellikleri
 * @returns {JSX.Element} - Autocomplete Input bileşeni
 */
const AutocompleteInput = forwardRef((props, ref) => {
  const {
    id,
    type = "text",
    value,
    onChange,
    placeholder,
    className,
    isValid = true,
    suggestions = [],
    showSuggestions = false,
    onSuggestionSelect,
    onBlur: externalOnBlur,
    ...otherProps
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Dışarı tıklama durumunda dropdown'ı kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        if (externalOnBlur) {
          externalOnBlur();
        }
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [externalOnBlur]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e) => {
    // Dropdown'a tıklanırsa input'un odağını kaybetmesini engellemek için
    // blur işlemini geciktiriyoruz
    setTimeout(() => {
      if (externalOnBlur && !suggestionsRef.current?.contains(document.activeElement)) {
        externalOnBlur(e);
      }
    }, 200);
  };

  const handleSuggestionClick = (suggestion) => {
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    }
  };

  return (
    <div className="relative w-full">
      <input
        id={id}
        ref={(node) => {
          // Hem dışarıdan gelen ref'i hem de kendi ref'imizi kullanabilmek için
          if (ref) {
            if (typeof ref === 'function') {
              ref(node);
            } else {
              ref.current = node;
            }
          }
          inputRef.current = node;
        }}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...otherProps}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg"
        >
          <ul className="max-h-60 overflow-auto py-1 text-base">
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                className="relative cursor-pointer select-none py-2 px-3 hover:bg-gray-100"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
});

AutocompleteInput.displayName = "AutocompleteInput";

export { AutocompleteInput }; 